import { Encounter, MaternityFile, Patient } from '../types'

export type ServiceSeriesKey =
  | 'register'
  | 'gyne'
  | 'general'
  | 'lab'
  | 'radiology'
  | 'maternity'

export type StatsRange = 'today' | 'week' | 'month'

type ServiceEvent = { key: ServiceSeriesKey; at: Date }

const DAY_MS = 24 * 60 * 60 * 1000

export const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate())

const toDate = (value?: string) => {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

export const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** Every real service event, each tagged with the date it happened. */
export function collectServiceEvents(
  patients: Patient[],
  encounters: Encounter[],
  maternityFiles: MaternityFile[],
): ServiceEvent[] {
  const events: ServiceEvent[] = []
  const push = (key: ServiceSeriesKey, value?: string) => {
    const at = toDate(value)
    if (at) events.push({ key, at })
  }
  patients.forEach((p) => push('register', p.registeredAt))
  encounters.forEach((e) => {
    if (e.consultationType === 'Gyne') push('gyne', e.startedAt)
    if (e.consultationType === 'General') push('general', e.startedAt)
    e.labRequests?.forEach((l) => {
      if (l.status === 'Completed') push('lab', l.completedAt || l.requestedAt)
    })
    e.radiologyRequests?.forEach((r) => {
      if (r.status === 'Completed' || r.report)
        push('radiology', r.completedAt || r.requestedAt)
    })
  })
  maternityFiles.forEach((m) => push('maternity', m.createdAt))
  return events
}

export function rangeStart(range: StatsRange, now = new Date()) {
  const today = startOfDay(now)
  if (range === 'today') return today
  if (range === 'week') return new Date(today.getTime() - 6 * DAY_MS)
  return new Date(today.getTime() - 29 * DAY_MS)
}

const emptyCounts = (): Record<ServiceSeriesKey, number> => ({
  register: 0,
  gyne: 0,
  general: 0,
  lab: 0,
  radiology: 0,
  maternity: 0,
})

export type DailyRow = { label: string; date: string } & Record<
  ServiceSeriesKey,
  number
>

/** One row per calendar day in the range, oldest first. */
export function dailyCounts(
  events: ServiceEvent[],
  range: StatsRange,
  now = new Date(),
): DailyRow[] {
  const start = rangeStart(range, now)
  const rows = new Map<string, DailyRow>()
  for (let t = start.getTime(); t <= startOfDay(now).getTime(); t += DAY_MS) {
    const d = new Date(t)
    rows.set(dayKey(d), {
      date: dayKey(d),
      label: d.toLocaleDateString(undefined, {
        weekday: range === 'month' ? undefined : 'short',
        day: 'numeric',
        month: 'short',
      }),
      ...emptyCounts(),
    })
  }
  events.forEach(({ key, at }) => {
    const row = rows.get(dayKey(at))
    if (row) row[key] += 1
  })
  return Array.from(rows.values())
}

export function totalsInRange(
  events: ServiceEvent[],
  range: StatsRange,
  now = new Date(),
) {
  const start = rangeStart(range, now).getTime()
  const totals = emptyCounts()
  events.forEach(({ key, at }) => {
    if (at.getTime() >= start && at.getTime() <= now.getTime()) totals[key] += 1
  })
  return totals
}

export type MonthlyRow = { label: string } & Record<ServiceSeriesKey, number>

/** Totals per month for the last `months` months, oldest first. */
export function monthlyCounts(
  events: ServiceEvent[],
  months = 6,
  now = new Date(),
): MonthlyRow[] {
  const rows: MonthlyRow[] = []
  const index = new Map<string, MonthlyRow>()
  for (let i = months - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const row: MonthlyRow = {
      label: d.toLocaleDateString(undefined, { month: 'short' }),
      ...emptyCounts(),
    }
    rows.push(row)
    index.set(`${d.getFullYear()}-${d.getMonth()}`, row)
  }
  events.forEach(({ key, at }) => {
    const row = index.get(`${at.getFullYear()}-${at.getMonth()}`)
    if (row) row[key] += 1
  })
  return rows
}
